import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ColDef,
  ICellRendererParams,
  ModuleRegistry,
  themeQuartz,
} from 'ag-grid-community';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { Button, ConfigProvider, DatePicker, Dropdown, Form, InputNumber, Modal, Select, message } from 'antd';
import type { MenuProps } from 'antd';
import dayjs from 'dayjs';
import TieringService from '../../services/TieringService';
import type {
  CreateRelationshipDto,
  EntityReltnDto,
  UpdateRelationshipDto,
} from '../../types/tiering.types';
import { usePartnershipContext } from '../../../../core/context/PartnershipContext';
import EntityService from '../../../EntityView/services/EntityService';
import type { EntityDto } from '../../../EntityView/services/EntityService';
import { darkFormTheme, MODAL_STYLES, MODAL_TITLE_STYLE } from '../../../../styles/modalStyles';
import styles from './TierList.module.css';

ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule]);

const gridTheme = themeQuartz.withParams({
  rowBorder: { style: 'solid', width: 1, color: 'rgba(255, 255, 255, 0.3)' },
});

interface TierListProps {
  entityId?: number;
}

const TierList: React.FC<TierListProps> = ({ entityId }) => {
  const [rows, setRows] = useState<EntityReltnDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<EntityReltnDto | null>(null);
  const [saving, setSaving] = useState(false);
  const [entities, setEntities] = useState<EntityDto[]>([]);
  const [form] = Form.useForm();
  const { selectedClient } = usePartnershipContext();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (entityId) {
        // Scoped to a specific entity — use the entity-specific endpoint
        const data = await TieringService.getRelationships(entityId);
        setRows(data);
      } else if (selectedClient) {
        // No entityId in URL: fetch all relationships, then filter to only those
        // belonging to entities under the selected client.
        const [allRelationships, clientEntities] = await Promise.all([
          TieringService.getAllRelationships(),
          EntityService.getEntitiesByClient(selectedClient.id),
        ]);
        const clientEntityIds = new Set(clientEntities.map((e) => e.id));
        const filtered = allRelationships.filter(
          (r) => clientEntityIds.has(r.startEntityId) || clientEntityIds.has(r.endEntityId),
        );
        setRows(filtered);
      } else {
        setRows([]);
      }
    } catch {
      message.error('Failed to load tiering relationships');
    } finally {
      setLoading(false);
    }
  }, [entityId, selectedClient]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!showModal || editing || !selectedClient?.id) return;
    EntityService.getEntitiesByClient(selectedClient.id)
      .then((data) => setEntities(data))
      .catch(() => setEntities([]));
  }, [showModal, editing, selectedClient]);

  const openCreate = useCallback(() => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ tierLevel: 1, entryDate: dayjs() });
    setShowModal(true);
  }, [form]);

  const openEdit = useCallback(
    (row: EntityReltnDto) => {
      setEditing(row);
      form.setFieldsValue({
        ownershipPct: row.ownershipPercent,
        relationshipDirection: row.relationshipDirection,
      });
      setShowModal(true);
    },
    [form],
  );

  const confirmDelete = useCallback(
    (row: EntityReltnDto) => {
      Modal.confirm({
        title: 'Delete Relationship',
        content: `Remove "${row.startEntityName} → ${row.endEntityName}"? This cannot be undone.`,
        okText: 'Delete',
        okType: 'danger',
        onOk: async () => {
          try {
            await TieringService.deleteRelationship(row.id);
            message.success('Relationship deleted');
            fetchData();
          } catch {
            message.error('Failed to delete relationship');
          }
        },
      });
    },
    [fetchData],
  );

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        const dto: UpdateRelationshipDto = {
          ownershipPercent: values.ownershipPct as number,
          relationshipDirection: values.relationshipDirection as string,
        };
        await TieringService.updateRelationship(editing.id, dto);
        message.success('Relationship updated');
      } else {
        const dto: CreateRelationshipDto = {
          startEntityId: values.startEntityId as number,
          endEntityId: values.endEntityId as number,
          ownershipPercent: values.ownershipPercent as number,
          relationshipDirection: values.relationshipDirection as string,
          tierLevel: values.tierLevel as number,
        };
        await TieringService.createRelationship(dto);
        message.success('Relationship created');
      }
      setShowModal(false);
      fetchData();
    } catch {
      message.error('Failed to save relationship');
    } finally {
      setSaving(false);
    }
  };

  const colDefs: ColDef<EntityReltnDto>[] = useMemo(
    () => [
      { field: 'startEntityName', headerName: 'Upper Tier Entity', flex: 2 },
      { field: 'endEntityName', headerName: 'Lower Tier Entity', flex: 2 },
      {
        field: 'ownershipPercent',
        headerName: 'Ownership %',
        flex: 1,
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: (p) =>
          p.value != null ? `${Number(p.value).toFixed(2)}%` : '',
      },
      { field: 'tierLevel', headerName: 'Tier Level', flex: 1 },
      { field: 'relationshipDirection', headerName: 'Direction', flex: 1 },
      {
        headerName: 'Actions',
        width: 100,
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams<EntityReltnDto>) => {
          if (!params.data) return null;
          const row = params.data;
          const items: MenuProps['items'] = [
            { key: 'edit', label: 'Edit', onClick: () => openEdit(row) },
            {
              key: 'delete',
              label: 'Delete',
              danger: true,
              onClick: () => confirmDelete(row),
            },
          ];
          return (
            <Dropdown menu={{ items }} trigger={['click']}>
              <Button size="small" type="text" style={{ color: '#ADADAD' }}>
                ⋯
              </Button>
            </Dropdown>
          );
        },
      },
    ],
    [openEdit, confirmDelete],
  );

  const defaultColDef: ColDef<EntityReltnDto> = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      minWidth: 100,
      enableRowGroup: true,
      enablePivot: true,
      enableValue: true,
    }),
    [],
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <button className={styles.btnAdd} onClick={openCreate}>
          + Add Relationship
        </button>
      </div>

      {loading ? (
        <div className={styles.loadingState}>
          <span className={styles.spinner} />
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.emptyState}>No tiering relationships found</div>
      ) : (
        <div className={styles.grid}>
          <AgGridReact
            rowData={rows}
            columnDefs={colDefs}
            defaultColDef={defaultColDef}
            theme={gridTheme}
            rowSelection="single"
            domLayout="autoHeight"
            sideBar={true}
            cellSelection={true}
            enableCellTextSelection={true}
            rowGroupPanelShow="always"
            groupDisplayType="multipleColumns"
            suppressMenuHide={false}
            statusBar={{
              statusPanels: [
                { statusPanel: 'agTotalAndFilteredRowCountComponent', align: 'left' as const },
                { statusPanel: 'agTotalRowCountComponent', align: 'center' as const },
                { statusPanel: 'agAggregationComponent' },
              ],
            }}
          />
        </div>
      )}

      <Modal
        title={<span style={MODAL_TITLE_STYLE}>{editing ? 'Edit Relationship' : 'Add Relationship'}</span>}
        open={showModal}
        onOk={handleSave}
        onCancel={() => setShowModal(false)}
        okText={editing ? 'Save' : 'Create'}
        okButtonProps={{ loading: saving }}
        styles={MODAL_STYLES}
      >
        <ConfigProvider theme={darkFormTheme}>
        <Form form={form} layout="vertical" className={styles.form}>
          {!editing ? (
            <>
              <Form.Item
                name="startEntityId"
                label="Upper Tier Entity"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select
                  showSearch
                  placeholder="Search and select entity..."
                  optionFilterProp="label"
                  options={entities.map((e) => ({
                    value: e.id,
                    label: `${e.name} (${e.entityType})`,
                  }))}
                  styles={{ popup: { root: { background: 'rgba(15,22,40,0.98)' } } }}
                />
              </Form.Item>
              <Form.Item
                name="endEntityId"
                label="Lower Tier Entity"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select
                  showSearch
                  placeholder="Search and select entity..."
                  optionFilterProp="label"
                  options={entities.map((e) => ({
                    value: e.id,
                    label: `${e.name} (${e.entityType})`,
                  }))}
                  styles={{ popup: { root: { background: 'rgba(15,22,40,0.98)' } } }}
                />
              </Form.Item>
              <Form.Item
                name="ownershipPercent"
                label="Ownership %"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                  step={0.01}
                  precision={2}
                  addonAfter="%"
                />
              </Form.Item>
              <Form.Item
                name="relationshipDirection"
                label="Direction"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select
                  placeholder="Select direction"
                  options={[
                    { value: 'Direct', label: 'Direct' },
                    { value: 'Indirect', label: 'Indirect' },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="tierLevel"
                label="Tier Level"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber style={{ width: '100%' }} min={1} precision={0} />
              </Form.Item>
              <Form.Item name="entryDate" label="Entry Date">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item
                name="ownershipPct"
                label="Ownership %"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber
                  min={0}
                  max={100}
                  precision={6}
                  style={{ width: '100%' }}
                  addonAfter="%"
                />
              </Form.Item>
              <Form.Item
                name="relationshipDirection"
                label="Direction"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select
                  placeholder="Select direction"
                  options={[
                    { value: 'Direct', label: 'Direct' },
                    { value: 'Indirect', label: 'Indirect' },
                  ]}
                />
              </Form.Item>
            </>
          )}
        </Form>
        </ConfigProvider>
      </Modal>
    </div>
  );
};

export default TierList;
